import React, { useState, useEffect, useRef, useContext } from 'react';
import Button from '@components/Button';
import cn from 'classnames';

import { navigate, useStaticQuery, graphql } from 'gatsby';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Configure, Stats } from 'react-instantsearch-dom';
import isEqual from 'lodash.isequal';
import qs from 'qs';
import PropTypes from 'prop-types';
import Link from '@utility/Link';
import SEO from '@components/seo';
import ProductAdvancedFilters from '@components/ProductSearch/advancedFilters';
import ProductFilterDropdown from '@components/ProductSearch/filterDropdown';
import ProductHits from '@components/ProductSearch/hits';
import ProductPagination from '@components/ProductSearch/pagination';
import ScrollInView from '@utility/ScrollInView';
import { titleCase } from '@helpers/stringFormat';
import foreach from 'lodash.foreach';
import {
  PRODUCTS_HITS_PER_PAGE,
  DEFAULT_TYPE_LABEL,
  TOOLTIP_IP,
  TOOLTIP_CERTIFICATION,
  TOOLTIP_DIMMING,
  TOOLTIP_ATTRS_IP,
  TOOLTIP_ATTRS_CERTIFICATION,
  TOOLTIP_ATTRS_DIMMING,
  TOOLTIP_ATTRS_INPUT,
  TOOLTIP_ATTRS_FILES,
} from '../../../common';
import * as styles from './styles';
import { viewItemListDL } from '@src/helpers/dataLayerHelper';
import { useLocation } from '@reach/router';
import { getCanonicalUrl, isParameterizedURL } from '@src/helpers/seoHelper';

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY,
);

const slugify = (type) => {
  return type
    .split(' ')
    .map(encodeURIComponent)
    .join('-');
};

const deslugify = (type) => {
  return type
    .split('-')
    .map(decodeURIComponent)
    .join(' ');
};

const maybeAddRefinementListParameters = (
  queryParameters,
  refinementList,
  refinements,
) => {
  foreach(refinements, (refinement) => {
    if (refinementList[refinement]) {
      queryParameters[refinement] = refinementList[refinement].map(
        encodeURIComponent,
      );
    }
  });

  return queryParameters;
};

// Handles taking the URL parameters and setting the search state to initialize with
const urlToSearchState = (location) => {
  const {
    page = 1,
    type = '',
    finish = '',
    collection = '',
    ipRating = [],
    certification = [],
    inputVoltage = [],
    dimmingControl = [],
    fileType = [],
    designer = [],
  } = qs.parse(location.search.slice(1));
  // `qs` does not return an array when there's a single value.
  const allIpRatings = Array.isArray(ipRating)
    ? ipRating
    : [ipRating].filter(Boolean);
  const allCertifications = Array.isArray(certification)
    ? certification
    : [certification].filter(Boolean);
  const allInputVoltages = Array.isArray(inputVoltage)
    ? inputVoltage
    : [inputVoltage].filter(Boolean);
  const allDimmingControls = Array.isArray(dimmingControl)
    ? dimmingControl
    : [dimmingControl].filter(Boolean);
  const allDownloads = Array.isArray(fileType)
    ? fileType
    : [fileType].filter(Boolean);
  const allDesigners = Array.isArray(designer)
    ? designer
    : [designer].filter(Boolean);

  return {
    page,
    menu: {
      'productType.title': decodeURIComponent(deslugify(type)),
      'productFinish.finish.code': decodeURIComponent(finish),
      'collection.title.title': decodeURIComponent(deslugify(collection)),
    },
    refinementList: {
      ipRating: allIpRatings.map(decodeURIComponent),
      certification: allCertifications.map(decodeURIComponent),
      inputVoltage: allInputVoltages.map(decodeURIComponent),
      dimmingControl: allDimmingControls.map(decodeURIComponent),
      fileType: allDownloads.map(decodeURIComponent),
      designer: allDesigners.map(decodeURIComponent),
    },
  };
};

const ProductSearch = ({
  location,
  defaultPath,
  secondaryDefaultLabel,
  advancedFiltersOpen,
  pageName,
}) => {
  const HITS_PER_PAGE = PRODUCTS_HITS_PER_PAGE;
  const canonicalURL = getCanonicalUrl(location.pathname);
  const DEBOUNCE_URL_TIME = 700;
  const filterDropdownRef = useRef();
  const typeDropdownRef = useRef();
  const allCollectionRef = useRef();
  const finishDropdownRef = useRef();
  const [finishCode, setFinishCode] = useState('');
  const [primaryDropdownLabel, setPrimaryDropdownLabel] = useState(
    DEFAULT_TYPE_LABEL,
  );
  const [isTypeDropdown, setTypeDropdown] = useState(false);
  const [secondaryDropdownLabel, setSecondaryDropdownLabel] = useState(
    secondaryDefaultLabel,
  );
  const [isFinishDropdown, setFinishDropdown] = useState(false);
  const [isAllCollection, setAllCollectionDropdown] = useState(false);
  const [isAdvancedMenu, setAdvancedMenu] = useState(advancedFiltersOpen);
  const [searchState, setSearchState] = useState(urlToSearchState(location));
  const [currentRefinments, setCurrentRefinements] = useState({});
  const [currentMenu, setCurrentMenu] = useState({});
  const [debouncedSetState, setDebouncedSetState] = useState(null);
  const [isLoading, setLoading] = useState(false);
  // const [showGateWall, setShowGateWall] = useState(false);

  // useEffect(() => {
  //   if (!localStorage.getItem("gatewallSubmit") && !authUser) {
  //     setTimeout(() => { 
  //       setShowGateWall(true)
  //     }, 3000)
  //   }
  // }, [])

  // useEffect(() => {
  //   if (showGateWall) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = 'auto';
  //   }

  //   return () => {
  //     document.body.style.overflow = 'auto';
  //   };
  // }, [showGateWall]);

  const finishesData = useStaticQuery(
    graphql`
      query {
        allSanityFinish(
          filter: { isHidden: { ne: true } }
          sort: { fields: orderRank, order: ASC }
        ) {
          edges {
            node {
              title
              code
              orderRank
              isHidden
              image {
                asset {
                  _id
                }
                altText
              }
              category {
                title
              }
            }
          }
        }
        allSanityFinishCategory(
          filter: { isHidden: { ne: true } }
          sort: { fields: orderRank, order: ASC }
          ) {
          edges {
            node {
              title
              orderRank
              isHidden
            }
          }
        }
      }
    `,
  );
  // useEffect(() => {
  //   // Logic to show gatewall based on URL
  //   const currentUrl = window.location.href;
  //   const shouldShowGateWall = finishesData.sanityGateWall.urls.some(url => currentUrl.includes(url));
  //   setShowGateWall(shouldShowGateWall);
  // }, [finishesData]);

  const createURL = (state) => {
    const isDefaultRoute =
      state.page === 1 &&
      state.menu &&
      !state.menu['productType.title'] &&
      !state.menu['collection.title.title'] &&
      !state.menu['productFinish.finish.code'] &&
      state.refinementList &&
      state.refinementList.ipRating.length === 0 &&
      state.refinementList.certification.length === 0 &&
      state.refinementList.inputVoltage.length === 0 &&
      state.refinementList.dimmingControl.length === 0 &&
      state.refinementList.fileType.length === 0 &&
      state.refinementList.designer.length === 0;

    if (isDefaultRoute) {
      return defaultPath;
    }

    const queryParameters = {};

    if (state.page !== 1) {
      queryParameters.page = state.page;
    }

    if (state.menu) {
      if (state.menu['productType.title']) {
        queryParameters.type = slugify(state.menu['productType.title']);
      }

      if (state.menu['productFinish.finish.code']) {
        queryParameters.finish = state.menu['productFinish.finish.code'];
      }

      if (state.menu['collection.title.title']) {
        queryParameters.collection = slugify(
          state.menu['collection.title.title'],
        );
      }
    }

    if (state.refinementList) {
      maybeAddRefinementListParameters(queryParameters, state.refinementList, [
        'ipRating',
        'certification',
        'inputVoltage',
        'dimmingControl',
        'fileType',
        'designer',
      ]);
    }

    const queryString = qs.stringify(queryParameters, {
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    });

    return `${ defaultPath }${ queryString }`;
  };

  // Handles creating the URL with parameters based on refinements
  const searchStateToUrl = (searchState) => {
    // console.log('Search State to URL:', searchState);
    return searchState ? createURL(searchState) : '';
  };

  // Handles click outside of dropdowns
  const dropdownClickHandler = (event) => {
    if (
      typeDropdownRef.current &&
      !typeDropdownRef.current.contains(event.target)
    ) {
      setTypeDropdown(false);
    }

    if (
      finishDropdownRef.current &&
      !finishDropdownRef.current.contains(event.target)
    ) {
      setFinishDropdown(false);
    }

    if (
      allCollectionRef.current &&
      !allCollectionRef.current.contains(event.target)
    ) {
      setAllCollectionDropdown(false);
    }
  };

  const scrollToFilters = () => {
    if (filterDropdownRef.current) {
      window.scrollTo({
        top: filterDropdownRef.current.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const typeDropdownHandler = () => {
    scrollToFilters();
    // Wait for scrolling to finish
    setTimeout(() => {
      setTypeDropdown(!isTypeDropdown);
    }, 300);
  };

  const finishDropdownHandler = () => {
    scrollToFilters();
    // Wait for scrolling to finish
    setTimeout(() => {
      setFinishDropdown(!isFinishDropdown);
    }, 300);
  };

  const allCollectionDropdownHandler = () => {
    scrollToFilters();
    // Wait for scrolling to finish
    setTimeout(() => {
      setAllCollectionDropdown(!isAllCollection);
    }, 300);
  };

  const advancedDropdownHandler = () => {
    setAdvancedMenu(!isAdvancedMenu);
  };

  // Maps Algolia-returned finish code to Sanity finish titles
  const getFinishTitle = (code) => {
    return finishesData.allSanityFinish.edges.find((finish) => {
      if (finish.node) {
        return finish.node.code === code;
      }
      return null;
    })?.node?.title;
  };

  const updateFilterLabels = (state) => {
    if (!state.menu) return false;

    const type = state.menu['productType.title'];
    const collection = state.menu['collection.title.title'];
    const finishCode = state.menu['productFinish.finish.code'];

    type && type != ''
      ? setPrimaryDropdownLabel(titleCase(type))
      : setPrimaryDropdownLabel(DEFAULT_TYPE_LABEL);

    collection && collection != ''
      ? setSecondaryDropdownLabel(titleCase(collection))
      : setSecondaryDropdownLabel(secondaryDefaultLabel);

    if (finishCode && finishCode != '') {
      setSecondaryDropdownLabel(getFinishTitle(finishCode));
      setFinishCode(finishCode);
    }

    if (!finishCode) {
      setFinishCode('');
    }
  };

  const onSearchStateChange = (updatedSearchState) => {
    // console.log('On Search State Change', updatedSearchState);
    updateFilterLabels(updatedSearchState);
    setTypeDropdown(false);
    setFinishDropdown(false);
    setAllCollectionDropdown(false);

    // To fake loading of product cards
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 100);

    // Pushes to and navigates refinement parameters to URL
    clearTimeout(debouncedSetState);
    setDebouncedSetState(
      setTimeout(() => {
        navigate(searchStateToUrl(updatedSearchState), {
          state: { scrollToType: window.pageYOffset },
        });
      }, DEBOUNCE_URL_TIME),
    );

    // console.log('Set Search State:', updatedSearchState);
    setSearchState(updatedSearchState);
  };

  useEffect(() => {
    // console.log('Use Effect:', searchState);
    setCurrentRefinements(searchState.refinementList);
    setCurrentMenu(searchState.menu);
    updateFilterLabels(searchState);
    document.addEventListener('mousedown', dropdownClickHandler);
  }, []);

  useEffect(() => {
    const updatedSearchState = urlToSearchState(location);
    const isBrowser = typeof window !== 'undefined';

    setCurrentRefinements(searchState.refinementList);
    setCurrentMenu(searchState.menu);

    if (
      (isBrowser &&
        Boolean(location.state) &&
        location.state.scrollToType > 0 &&
        !isEqual(currentRefinments, updatedSearchState.refinementList)) ||
      !isEqual(currentMenu, updatedSearchState.menu)
    ) {
      window.scrollTo(0, location?.state?.scrollToType);
    }

    updateFilterLabels(updatedSearchState);
    setSearchState(updatedSearchState);
  }, [location]);

  const handleEscKeyDown = (keyDownEvent) => {
    if (['Escape', 'Esc'].includes(keyDownEvent.key) && (isTypeDropdown || isFinishDropdown || isAllCollection)) {
      if (isTypeDropdown) {
        setTypeDropdown(false)
      }
      if (isFinishDropdown) {
        setFinishDropdown(false)
      }
      if (isAllCollection) {
        setAllCollectionDropdown(false)
      }
    }
  }

  useEffect(() => {
    if (window) {
      window.addEventListener('keydown', handleEscKeyDown)
    }
    return () => {
      if (window) {
        window.removeEventListener('keydown', handleEscKeyDown)
      }
    }
  })

  return (
    <div ref={ filterDropdownRef } className="md:pt-12 mb-16">
      <SEO
        title={
          primaryDropdownLabel && secondaryDropdownLabel
            ? `${ primaryDropdownLabel } - ${ secondaryDropdownLabel }${ pageName }`
            : `All Luminaires${ pageName }`
        }
      >
        <link rel="canonical" href={ canonicalURL } />
        { isParameterizedURL(location.search) && (
          <meta name="robots" content="noindex" />
        ) }
      </SEO>
      {/* <GateWall
        open={ showGateWall }
        data={ finishesData.allSanityGateWall.edges[0].node }
        close={ () => setShowGateWall(false) }
      /> */}
      <ScrollInView className={ "overflow-hidden" } animateType="fadeInUp">
        <InstantSearch
          searchState={ searchState }
          onSearchStateChange={ onSearchStateChange }
          createURL={ createURL }
          searchClient={ searchClient }
          indexName={ `${ process.env.GATSBY_ALGOLIA_PREFIX }products` }
        // TODO change to prop
        >
          <Configure hitsPerPage={ HITS_PER_PAGE } />
          <div className="wrapper">
            <div className="type-sans-630 md:type-sans-930">
              <h1 className='inline-block'>I&apos;m looking for</h1>
              <ProductFilterDropdown
                ref={ typeDropdownRef }
                label={ primaryDropdownLabel }
                isOpen={ isTypeDropdown }
                toggleMenu={ typeDropdownHandler }
                attribute="productType.title"
                defaultLabel={ DEFAULT_TYPE_LABEL }
                showClearButton={ true }
                isAllSelected={ primaryDropdownLabel === DEFAULT_TYPE_LABEL }
              />
              <br />
              in
              {/* {defaultPath === '/type' ? (
                <ProductFilterDropdown
                  ref={finishDropdownRef}
                  label={secondaryDropdownLabel}
                  isOpen={isFinishDropdown}
                  toggleMenu={finishDropdownHandler}
                  type="finish"
                  productFinishes={finishesData.allSanityFinish.edges}
                  finishCategoriesData={
                    finishesData.allSanityFinishCategory.edges
                  }
                />
              ) : ( */}
              <ProductFilterDropdown
                ref={ allCollectionRef }
                label={ secondaryDropdownLabel }
                isOpen={ isAllCollection }
                toggleMenu={ allCollectionDropdownHandler }
                attribute="collection.title.title"
                attribute2="productFinish.finish.code"
                defaultLabel={ secondaryDefaultLabel }
                showClearButton={ true }
                isAllSelected={ secondaryDropdownLabel === secondaryDefaultLabel }
              />
              {/* )} */ }
            </div>
          </div>

          <div className="wrapper">
            { defaultPath === '/type' &&
              <ProductFilterDropdown
                ref={ finishDropdownRef }
                label={ secondaryDropdownLabel }
                isOpen={ isFinishDropdown }
                toggleMenu={ finishDropdownHandler }
                type="finish"
                productFinishes={ finishesData.allSanityFinish.edges }
                finishCategoriesData={
                  finishesData.allSanityFinishCategory.edges
                }
              />
            }
          </div>

          { defaultPath === '/type' ? (
            <ProductAdvancedFilters
              isOpen={ isAdvancedMenu }
              toggleMenu={ advancedDropdownHandler }
              refinements={ [
                {
                  title: 'IP Rating',
                  attribute: 'ipRating',
                  tooltip: TOOLTIP_IP,
                  tooltipAttrs: TOOLTIP_ATTRS_IP,
                },
                {
                  title: 'Compliance',
                  attribute: 'certification',
                  tooltip: TOOLTIP_CERTIFICATION,
                  tooltipAttrs: TOOLTIP_ATTRS_CERTIFICATION,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
                {
                  title: 'Input Voltage',
                  attribute: 'inputVoltage',
                  tooltipAttrs: TOOLTIP_ATTRS_INPUT,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
                {
                  title: 'Dimming Control',
                  attribute: 'dimmingControl',
                  tooltip: TOOLTIP_DIMMING,
                  tooltipAttrs: TOOLTIP_ATTRS_DIMMING,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
              ] }
            />
          ) : (
            <ProductAdvancedFilters
              isOpen={ isAdvancedMenu }
              toggleMenu={ advancedDropdownHandler }
              refinements={ [
                { title: 'Designer', attribute: 'designer' },
                {
                  title: 'File Type',
                  attribute: 'fileType',
                  tooltipAttrs: TOOLTIP_ATTRS_FILES,
                },
                {
                  title: 'IP Rating',
                  attribute: 'ipRating',
                  tooltip: TOOLTIP_IP,
                  tooltipAttrs: TOOLTIP_ATTRS_IP,
                },
                {
                  title: 'Compliance',
                  attribute: 'certification',
                  tooltip: TOOLTIP_CERTIFICATION,
                  tooltipAttrs: TOOLTIP_ATTRS_CERTIFICATION,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
                {
                  title: 'Input Voltage',
                  attribute: 'inputVoltage',
                  tooltipAttrs: TOOLTIP_ATTRS_INPUT,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
                {
                  title: 'Dimming Control',
                  attribute: 'dimmingControl',
                  tooltip: TOOLTIP_DIMMING,
                  tooltipAttrs: TOOLTIP_ATTRS_DIMMING,
                  // WARNING: if changing advanced filter labels make sure the associated key is changes in common.js
                },
              ] }
            />
          ) }


          <Stats
            translations={ {
              stats(nbHits) {
                if (nbHits === 0) {
                  return (
                    <div className="wrapper type-sans-430 md:type-sans-530 text-mono-500">
                      <p>Whoops, it looks like no products match.</p>
                      <p>
                        <Link to={ defaultPath } aria-label="Reset your selection">Reset</Link> your selection.
                      </p>
                    </div>
                  );
                }
                return null;
              },
            } }
          />
          <ProductHits
            finishCode={ finishCode }
            isLoading={ isLoading }
            type={ defaultPath }
          />
          {/* Commenting out to implement infinte scroll */ }
          {/* <div className="wrapper">
            <ProductPagination />
          </div> */}
        </InstantSearch>
      </ScrollInView>
    </div>
  );
};

ProductSearch.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
    state: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  defaultPath: PropTypes.string.isRequired,
  secondaryDefaultLabel: PropTypes.string.isRequired,
  advancedFiltersOpen: PropTypes.bool.isRequired,
  pageName: PropTypes.string,
};

ProductSearch.defaultProps = {
  pageName: '',
};

export default ProductSearch;
